import { useState, useEffect, useRef } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, Search, ChevronDown } from "lucide-react";
import JusticeEmblem from "@/components/JusticeEmblem";
import LogoEmblem from "@/components/LogoEmblem";

const MEGA = {
  RESEARCH: {
    color: "gold",
    items: [
      ["/research", "Research Library", "Briefs, sources, dossiers"],
      ["/dashboard", "Global Research Map", "Depth-layered investigation"],
    ],
  },
  TOOLS: {
    color: "gold",
    items: [
      ["/ai-assistant", "IOCRI AI Assistant", "Educational research chat"],
      ["/scam-detection", "Scam Analysis", "Signal review & pattern analysis"],
    ],
  },
  LEARN: {
    color: "gold",
    items: [
      ["/education", "Learning Centre", "Source-verified guides"],
      ["/awareness", "Know the Signals", "Scroll-driven investigations"],
    ],
  },
  IMPACT: {
    color: "crimson",
    items: [
      ["/victim-support", "Victim Support", "Verified reporting resources"],
      ["/social-media", "IOCRI Social", "Updates & campaigns"],
    ],
  },
  ENGAGE: {
    label: "GET INVOLVED",
    color: "gold",
    items: [
      ["/volunteer", "Volunteer", "Contribute to the research network"],
      ["/contact", "Contact", "Enquiries, media, collaboration"],
    ],
  },
  ABOUT: {
    color: "gold",
    items: [
      ["/about", "About IOCRI", "Follow the evidence"],
      ["/authors/shriya-das", "Founder", "Shriya Das"],
    ],
  },
};

export default function Header() {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const location = useLocation();
  const closeTimer = useRef(null);

  useEffect(() => { setOpen(false); setActive(null); }, [location.pathname]);

  const openMenu = (k) => { clearTimeout(closeTimer.current); setActive(k); };
  const scheduleClose = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setActive(null), 200);
  };

  return (
    <header className="sticky top-0 z-50 glass-strong" data-testid="site-header">
      {/* Emergency notice bar */}
      <div className="bg-black/80 text-[10px] tracking-[0.3em] uppercase text-gold/80 border-b border-white/5 font-mono">
        <div className="max-w-7xl mx-auto px-6 py-1.5 flex items-center justify-between">
          <span data-testid="header-notice">In distress? Call your local emergency number · UN Trafficking Hotline available</span>
          <span className="hidden md:block">IOCRI · Est. 2026 · Global</span>
        </div>
      </div>

      {/* Emblems */}
      <div className="flex items-center justify-center pt-3 pb-1">
        <Link to="/" data-testid="header-emblem-link" className="flex flex-col items-center gap-1">
          <div className="flex items-center gap-3">
            <JusticeEmblem size="md" />
            <LogoEmblem size="md" />
          </div>
          <span className="text-[10px] font-mono tracking-[0.35em] text-gold/80">IOCRI</span>
        </Link>
      </div>

      {/* Main nav row */}
      <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between gap-6" onMouseLeave={scheduleClose}>
        <Link to="/" className="hidden md:flex flex-col leading-tight" data-testid="header-brand">
          <span className="font-display text-sm font-medium text-white tracking-[0.15em]">INTERNATIONAL ORGANIZED</span>
          <span className="font-display text-sm font-medium tracking-[0.15em]" style={{ color: "#C1121F" }}>CRIME RESEARCH INSTITUTE</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1 text-xs font-mono tracking-[0.25em] relative">
          {Object.keys(MEGA).map((k) => {
            const m = MEGA[k];
            const label = m.label || k;
            const isCrimson = m.color === "crimson";
            return (
              <div
                key={k}
                onMouseEnter={() => openMenu(k)}
                className="relative"
                data-testid={`nav-${k.toLowerCase()}`}
              >
                <button
                  className={`px-3 py-2 flex items-center gap-1 transition-colors ${
                    active === k ? (isCrimson ? "text-crimson" : "text-gold") : "text-white/70 hover:text-white"
                  }`}
                  style={active === k && isCrimson ? { color: "#C1121F" } : undefined}
                >
                  {label}
                  <ChevronDown className={`h-3 w-3 transition-transform ${active === k ? "rotate-180" : ""}`} />
                </button>
                {/* signal underline */}
                <span
                  aria-hidden
                  className="absolute left-3 right-3 -bottom-[1px] h-px transition-all"
                  style={{
                    background: isCrimson ? "#C1121F" : "#00D4FF",
                    opacity: active === k ? 1 : 0,
                  }}
                />
                {active === k && (
                  <div
                    className="absolute top-full left-0 mt-0 pt-4 w-[380px] z-50"
                    onMouseEnter={() => openMenu(k)}
                    onMouseLeave={scheduleClose}
                  >
                    <div className="bg-obsidian-soft border border-white/10 rounded-sm shadow-2xl p-2" data-testid={`mega-${k.toLowerCase()}`}>
                      {m.items.map(([to, title, desc]) => (
                        <NavLink
                          key={to}
                          to={to}
                          data-testid={`mega-item-${title.toLowerCase().replace(/\s+/g, "-")}`}
                          className="block px-4 py-3 rounded-sm hover:bg-white/5 group transition-colors"
                        >
                          <div className="text-white group-hover:text-gold text-sm font-display tracking-wide">{title}</div>
                          <div className="text-white/40 text-[10px] mt-0.5 font-mono tracking-widest">{desc}</div>
                        </NavLink>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="flex items-center gap-2">
          <Link to="/search" data-testid="header-search" className="hidden lg:flex h-9 w-9 items-center justify-center rounded-sm border border-white/10 hover:border-gold/40 text-white/70 hover:text-gold" aria-label="Search">
            <Search className="h-4 w-4" />
          </Link>
          <button
            data-testid="header-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2 rounded-sm border border-white/10"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5 text-gold" /> : <Menu className="h-5 w-5 text-gold" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden border-t border-white/5 bg-black/95" data-testid="mobile-menu">
          <div className="max-w-7xl mx-auto px-6 py-4 space-y-4">
            {Object.keys(MEGA).map((k) => {
              const m = MEGA[k];
              const label = m.label || k;
              return (
                <div key={k}>
                  <div className="text-[10px] font-mono tracking-[0.3em] mb-2" style={{ color: m.color === "crimson" ? "#C1121F" : "#00D4FF" }}>
                    {label}
                  </div>
                  <div className="space-y-1 pl-2 border-l border-white/10">
                    {m.items.map(([to, title]) => (
                      <NavLink
                        key={to}
                        to={to}
                        onClick={() => setOpen(false)}
                        data-testid={`mnav-${title.toLowerCase().replace(/\s+/g, "-")}`}
                        className={({ isActive }) => `block py-1.5 text-sm ${isActive ? "text-gold" : "text-white/80"}`}
                      >
                        {title}
                      </NavLink>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </header>
  );
}
